/**
 * Utilities for partial-day time overlap detection.
 * Used by AddTimeToRequest to prevent conflicting partial-day time requests.
 */

/**
 * Parse an ISO date-time string (e.g., "2024-01-15T09:30:00Z") to minutes
 * since midnight in UTC. Mirrors the UTC extraction performed by formatUTCTo12Hour.
 *
 * @param isoString - ISO 8601 date-time string from the API.
 * @returns Minutes since midnight (UTC).
 */
export function isoToUTCMinutes(isoString: string): number {
  const d = new Date(isoString);
  return d.getUTCHours() * 60 + d.getUTCMinutes();
}

/**
 * Convert a 12-hour time-fields object (hour/minute/ampm DropdownOption values) to
 * minutes since midnight. Mirrors the logic in validationEngine.ts `timeToMinutes`.
 *
 * @param time - Object with optional `hour`, `minute`, and `ampm` value containers.
 * @returns Minutes since midnight, or `null` when any required field is absent.
 */
export function timeFieldsToMinutes(time: {
  hour?: { value?: string };
  minute?: { value?: string };
  ampm?: { value?: string };
}): number | null {
  if (!time.hour?.value || !time.minute?.value || !time.ampm?.value) return null;
  let h = Number.parseInt(time.hour.value, 10);
  const m = Number.parseInt(time.minute.value, 10);
  const ampm = time.ampm.value.toUpperCase();
  if (ampm === 'PM' && h !== 12) h += 12;
  else if (ampm === 'AM' && h === 12) h = 0;
  return h * 60 + m;
}

/**
 * Returns `true` when two time ranges strictly overlap.
 * Touching boundaries (range A ends at the exact minute range B starts) are
 * treated as non-overlapping so back-to-back partial requests are permitted.
 *
 * @param a - [startMinutes, endMinutes] for the first range.
 * @param b - [startMinutes, endMinutes] for the second range.
 */
export function doTimeRangesOverlap(
  a: [number, number],
  b: [number, number]
): boolean {
  return a[0] < b[1] && b[0] < a[1];
}
